import { useCallback, useState, FC } from "react";

import { Card, Col, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { FieldValues, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { Image } from "primereact/image";

import Input from "@/common/components/form/Input";
import SearchAddress from "@/common/components/form/SearchAddress";
import MapComponent from "@/common/components/map/MapComponent";
import { Permissions } from "@/common/enums/sharedEnums.ts";
import { phoneRule, urlRule } from "@/common/formValidation/index.ts";

import edit from "../../../assets/svg/edit.svg";
import history from "../../../routes/history";
import { useCreateGroup, useUpdateGroup } from "../hooks/group.hooks";
import { IGroup } from "../interfaces/group";
import { GROUP, MANAGEMENT } from "../routes/ProfileRoutes";
import { uploadGroupLogo } from "../services";

interface IGroupFormProps {
  defaultValues?: IGroup;
  readOnly?: boolean;
}

const GroupForm: FC<IGroupFormProps> = ({ defaultValues, readOnly }) => {
  const { handleSubmit, control, watch, setValue } = useForm<FieldValues>({
    defaultValues,
  });

  const [logoUrl, setLogoUrl] = useState<string>(watch("logo.url"));

  const { mutate: createGroup } = useCreateGroup({
    handleSuccess: (data) => {
      toast.success(`Le groupe ${data?.label}  "a été ajouté" !`);
    },
    handleError: () => {
      toast.error("Le groupe n'a pas pu être créé");
    },
  });

  const { mutate: updateGroup } = useUpdateGroup({
    handleSuccess: (data) => {
      toast.success(`Le groupe ${data?.label} a été mis à jour !`);
    },
    handleError: () => {
      toast.error("Le groupe n'a pas pu être mis à jour");
    },
  });

  const onSubmit = async (data: any) => {
    try {
      delete data.agencies;
      if (data?.id) {
        updateGroup(data);
      } else {
        createGroup(data);
      }
      history.push(MANAGEMENT);
    } catch (e: any) {
      toast.error("Impossible de valider le formulaire");
    }
  };

  const uploadHandler = async (data: any) => {
    const [file] = data.files;
    const fileReader = new FileReader();

    const formData = new FormData();
    formData.append("logo", file);

    await uploadGroupLogo(watch("id"), formData).then(({ url }: any) => {
      setLogoUrl(url);
    });
    fileReader.readAsDataURL(file);

    toast.success(`Le logo a été mis à jour !`);
  };

  const MapRender = useCallback(() => {
    const addressData = watch("address");
    return (
      <MapComponent
        coordinates={
          addressData?.latitudeY && addressData?.longitudeX
            ? {
                lat: Number(addressData?.latitudeY),
                lng: Number(addressData?.longitudeX),
              }
            : undefined
        }
        style={{
          borderRadius: "16px",
          flex: 1,
        }}
      />
    );
  }, [watch("address.latitudeY"), watch("address.longitudeX")]);

  return (
    <div className="bg-container groupForm">
      <div className="d-flex justify-content-between">
        <div className=" d-flex gap-2 mb-2">
          {logoUrl && <Image src={logoUrl} alt="Image" width="50" preview />}
          <h2
            className="mt-1 fw-bold align-self-center"
            style={{ fontSize: "24px" }}
          >
            {defaultValues ? `Groupe ${defaultValues.label}` : "Nouveau Groupe"}
          </h2>
        </div>

        <div className="d-flex align-items-center">
          {watch("id") && !readOnly && (
            <FileUpload
              auto
              mode="basic"
              name="logo"
              accept="image/*"
              maxFileSize={2000000}
              customUpload
              uploadHandler={uploadHandler}
              chooseLabel="Ajouter / Modifier le logo"
              className="me-2"
            />
          )}
          {readOnly &&
            defaultValues?.permissions.includes(Permissions.GROUP_UPDATE) && (
              <Link
                to={{
                  pathname: GROUP.replace(":id?", defaultValues?.id || ""),
                  state: { readOnly: false },
                }}
                replace
                className="btn btn-actions btn-edit-order"
                style={{ height: "36px", width: "36px" }}
              >
                <img
                  src={edit}
                  alt="edit"
                  style={{ width: "12px", height: "12px" }}
                />
              </Link>
            )}
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="groupForm-form">
        <div className="groupForm-form-details">
          <Input
            control={control}
            label="Nom"
            name={"label"}
            defaultValue=""
            rules={{ required: "Veuillez renseigner un nom" }}
            disabled={readOnly}
          />
          <Input
            control={control}
            label="Email"
            name={"email"}
            defaultValue=""
            disabled={readOnly}
          />
          <Input
            control={control}
            label="Téléphone"
            name={"phone"}
            defaultValue=""
            disabled={readOnly}
            rules={{
              ...phoneRule,
            }}
          />
          <Input
            control={control}
            label="Lien WhatsApp"
            name={"whatsAppUrl"}
            defaultValue=""
            disabled={readOnly}
            rules={{
              ...urlRule,
            }}
          />
          <Input
            control={control}
            label="Siren"
            name={"siren"}
            defaultValue=""
            rules={{ required: "Veuillez renseigner un siren" }}
            disabled={readOnly}
          />
        </div>
        <Card>
          <Card.Header>Emplacement</Card.Header>
          <Card.Body className="groupForm-form-address">
            <div className="groupForm-form-address-inputs">
              {readOnly ? (
                <Input
                  control={control}
                  label="Adresse postale"
                  name="address"
                  defaultValue={
                    defaultValues?.address &&
                    `${defaultValues.address.address} ${defaultValues.address.zip} ${defaultValues.address.city}`
                  }
                  disabled={true}
                />
              ) : (
                <SearchAddress
                  control={control}
                  name={`address`}
                  rules={{ required: "veuillez indiquer une adresse" }}
                  label="Adresse postale"
                  isDisabled={false}
                  defaultAddress=""
                />
              )}

              <Input
                control={control}
                label="Adresse additionnelle"
                name={"address.additionalAddress"}
                defaultValue=""
                disabled={readOnly}
              />
              <Input
                control={control}
                label="Digicode"
                name={"address.digicode"}
                defaultValue=""
                disabled={readOnly}
              />
              <Input
                control={control}
                label="Résidence"
                name={"address.residenceName"}
                defaultValue=""
                disabled={readOnly}
              />
              <Input
                control={control}
                label="Assurance"
                name={"insurance"}
                defaultValue=""
                disabled={readOnly}
              />

              <Row>
                <Col md={6}>
                  <Input
                    control={control}
                    label="Numéro de police"
                    name={"policeNumber"}
                    defaultValue=""
                    disabled={readOnly}
                  />
                </Col>
                <Col md={6} className={"form-input"}>
                  <label className="form-input_label">Date de validité</label>
                  <DatePicker
                    className="form-input_field date-select w-100 px-2"
                    dateFormat="dd/MM/yyyy"
                    locale="fr"
                    selected={
                      watch("policeDate")
                        ? new Date(watch("policeDate"))
                        : undefined
                    }
                    onChange={(date) => setValue("policeDate", date)}
                    formatWeekDay={(nameOfDay) =>
                      nameOfDay.toString().toUpperCase().substr(0, 1)
                    }
                  />
                </Col>
              </Row>
            </div>
            <MapRender />
          </Card.Body>
        </Card>
        {!readOnly && (
          <div className="d-flex justify-content-start">
            <Button type={"submit"}>
              {defaultValues ? "Modifier" : "Ajouter"}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default GroupForm;
